import { useState, useEffect, useRef, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useSpeech() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const audioRef = useRef(null);
  const speechCancelledRef = useRef(false);
  const cachedVoiceRef = useRef(null);
  const ttsFailedRef = useRef(false);

  // ── Web Speech API voice selection (fallback) ──
  const selectPreferredVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    const femaleNames = [
      'Samantha', 'Karen', 'Moira', 'Tessa', 'Fiona', 'Victoria',
      'Microsoft Zira', 'Microsoft Jenny', 'Microsoft Aria',
      'Google UK English Female', 'Google US English',
    ];
    return voices.find(v => femaleNames.some(name => v.name.includes(name)))
      || voices.find(v => v.lang.startsWith('en') && /female|woman/i.test(v.name))
      || voices.find(v => v.lang.startsWith('en'));
  }, []);

  useEffect(() => {
    const cacheVoice = () => {
      const voice = selectPreferredVoice();
      if (voice) cachedVoiceRef.current = voice;
    };
    cacheVoice();
    window.speechSynthesis.onvoiceschanged = () => { cacheVoice(); };
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      speechCancelledRef.current = true;
      window.speechSynthesis.cancel();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [selectPreferredVoice]);

  const getPreferredVoice = useCallback(() => {
    if (cachedVoiceRef.current) return cachedVoiceRef.current;
    const voice = selectPreferredVoice();
    if (voice) cachedVoiceRef.current = voice;
    return voice;
  }, [selectPreferredVoice]);

  // ── Fallback: Web Speech API ──
  const fallbackSpeak = useCallback((text, onEnd) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.75;
    utterance.pitch = 1.0;
    utterance.volume = 0.9;
    const preferred = getPreferredVoice();
    if (preferred) utterance.voice = preferred;
    if (onEnd) utterance.onend = onEnd;
    utterance.onerror = () => { if (onEnd) onEnd(); };
    window.speechSynthesis.speak(utterance);
    return utterance;
  }, [getPreferredVoice]);

  // ── Generate TTS audio URL via backend ──
  const generateTTSUrl = useCallback(async (text, voice = "coral") => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/audio/tts`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ text, voice }),
    });
    if (!res.ok) throw new Error(`TTS failed: ${res.status}`);
    const data = await res.json();
    return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
  }, []);

  // ── Play an audio URL, returns Promise ──
  const playAudioUrl = useCallback((url) => {
    return new Promise((resolve, reject) => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { audioRef.current = null; resolve(); };
      audio.onerror = (e) => { audioRef.current = null; reject(e); };
      audio.play().catch(reject);
    });
  }, []);

  // ── speakText: single text (used by guided practice) ──
  const speakText = useCallback((text, onEnd) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    window.speechSynthesis.cancel();

    if (ttsFailedRef.current) {
      fallbackSpeak(text, onEnd);
      return;
    }

    generateTTSUrl(text)
      .then((url) => {
        if (speechCancelledRef.current) { if (onEnd) onEnd(); return; }
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { audioRef.current = null; if (onEnd) onEnd(); };
        audio.onerror = () => {
          audioRef.current = null;
          ttsFailedRef.current = true;
          fallbackSpeak(text, onEnd);
        };
        audio.play().catch(() => {
          ttsFailedRef.current = true;
          fallbackSpeak(text, onEnd);
        });
      })
      .catch(() => {
        ttsFailedRef.current = true;
        fallbackSpeak(text, onEnd);
      });
  }, [generateTTSUrl, fallbackSpeak]);

  // ── Stop everything ──
  const stopSpeech = useCallback(() => {
    speechCancelledRef.current = true;
    window.speechSynthesis.cancel();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setIsSpeaking(false);
    setIsPaused(false);
    setIsGenerating(false);
    setCurrentStep(-1);
  }, []);

  // ── speakInstructions: pre-generate ALL step audio in parallel, then play sequentially ──
  const speakInstructions = useCallback(async (instructions) => {
    if (!instructions || !Array.isArray(instructions) || instructions.length === 0) return;
    stopSpeech();
    speechCancelledRef.current = false;
    setIsGenerating(true);
    setIsSpeaking(true);
    setIsPaused(false);
    setCurrentStep(0);

    if (!ttsFailedRef.current) {
      try {
        // Pre-generate ALL audio URLs in parallel
        const texts = instructions.map((inst, i) => `Step ${i + 1}. ${inst}`);
        const urls = await Promise.all(texts.map(t => generateTTSUrl(t)));

        if (speechCancelledRef.current) return;
        setIsGenerating(false);

        // Play sequentially — no wait between steps (audio is pre-loaded)
        for (let i = 0; i < urls.length; i++) {
          if (speechCancelledRef.current) break;
          setCurrentStep(i);
          await playAudioUrl(urls[i]);
          // Brief pause between steps
          if (i < urls.length - 1 && !speechCancelledRef.current) {
            await new Promise((r) => setTimeout(r, 600));
          }
        }
      } catch (err) {
        ttsFailedRef.current = true;
        setIsGenerating(false);
        // Fallback: play remaining steps with Web Speech API
        if (!speechCancelledRef.current) {
          await speakInstructionsFallback(instructions);
          return;
        }
      }
    } else {
      setIsGenerating(false);
      await speakInstructionsFallback(instructions);
      return;
    }

    if (!speechCancelledRef.current) {
      setIsSpeaking(false);
      setIsPaused(false);
      setCurrentStep(-1);
    }
  }, [stopSpeech, generateTTSUrl, playAudioUrl]);

  // ── Fallback: Web Speech API for all instructions ──
  const speakInstructionsFallback = useCallback(async (instructions) => {
    for (let i = 0; i < instructions.length; i++) {
      if (speechCancelledRef.current) break;
      setCurrentStep(i);
      const text = `Step ${i + 1}. ${instructions[i]}`;
      await new Promise((resolve) => { fallbackSpeak(text, resolve); });
      if (i < instructions.length - 1 && !speechCancelledRef.current) {
        await new Promise((r) => setTimeout(r, 600));
      }
    }
    if (!speechCancelledRef.current) {
      setIsSpeaking(false);
      setIsPaused(false);
      setCurrentStep(-1);
    }
  }, [fallbackSpeak]);

  // ── Pause / Resume ──
  const togglePauseSpeech = useCallback(() => {
    if (audioRef.current) {
      if (isPaused) {
        audioRef.current.play().catch(() => {});
        setIsPaused(false);
      } else {
        audioRef.current.pause();
        setIsPaused(true);
      }
    } else {
      if (isPaused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
      } else {
        window.speechSynthesis.pause();
        setIsPaused(true);
      }
    }
  }, [isPaused]);

  return {
    isSpeaking, isPaused, isGenerating, currentStep,
    speakText, speakInstructions, stopSpeech, togglePauseSpeech,
    speechCancelledRef, getPreferredVoice
  };
}
